import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ThinkingStep } from '@shared';
import { ChatStreamState } from '../../store';
import { OmarkComponent } from './omark.component';
import { ReflexionBlockComponent } from './reflexion-block.component';
import { StreamedTextComponent } from './streamed-text.component';

/**
 * The SB-3651 simulation's beats (its `run()`), in ms. Real steps arrive
 * whenever the model produces them; these only set the pace at which they
 * are SHOWN, so the sequence always reads like the artifact.
 */
const BEATS = {
  /** The mark turns alone before the header appears. */
  markAlone: 1400,
  /** Header shown → its first step. */
  firstStep: 900,
  /** Two steps that land together still enter one after the other. */
  stepGap: 450,
  /** A finished group: header past tense, folds (250), then the next step (300). */
  groupClose: 550,
  /** Last step → everything folds. */
  doneBeat: 200,
  /** The fold itself (`--motion-base`); the answer streams once it is over. */
  fold: 400,
  /** A live row keeps its in-progress wording at least this long. */
  liveHold: 1000,
  /** A finished answer that never finishes revealing is let go after this. */
  maxHold: 30_000,
} as const;

type Phase = 'mark' | 'thinking' | 'folding' | 'answer';

/**
 * Whether the first `count` steps end on a group row — the block's own rule:
 * searches and documents group from two in a row.
 */
const endsOnGroup = (steps: ThinkingStep[], count: number): boolean => {
  const kind = steps[count - 1]?.kind;
  return (
    (kind === 'web_search' || kind === 'document') &&
    steps[count - 2]?.kind === kind
  );
};

/**
 * SB-3651 — one live turn, sequenced exactly like the simulation: the mark
 * turns alone in its column; the header fades in; steps appear one by one
 * (a group finishes and folds before the next kind of step); when the answer
 * is ready everything folds at once — the mark fades, the column closes, the
 * trace collapses and its header becomes the summary — and only THEN does the
 * answer stream, word by word, full width.
 *
 * `closed` means the answer has landed. The page keeps this turn on screen
 * until the choreography is over and {@link settled} fires, and only then
 * shows the saved reply — so an answer that arrived in one burst still folds
 * first and streams after.
 */
@Component({
  selector: 'squadbox-thinking-turn',
  imports: [
    TranslateModule,
    OmarkComponent,
    ReflexionBlockComponent,
    StreamedTextComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="turn" [class.folded]="folded()">
      <div class="mark">
        <squadbox-omark [working]="phase() !== 'answer'" />
      </div>
      <div class="body">
        <!-- The ONLY live region here, and it never changes after it
             appears: a screen reader is told once that the answer is being
             written. The streamed text is deliberately outside it. -->
        <span class="sr-only" role="status">
          {{ 'CHAT.STREAMING_ANSWER' | translate }}
        </span>
        @if (headerShown()) {
          <squadbox-reflexion-block
            class="header-in"
            [steps]="shownSteps()"
            [startedAt]="stream().startedAt"
            [answerStartedAt]="foldedAnswerAt()"
            [summary]="stream().summary"
            [lastGroupOpen]="lastGroupOpen()"
            [live]="true"
            [inertLinks]="inertLinks()"
          />
        } @else if (!folded()) {
          <!-- Before the header's beat, its place is held so the mark already
               sits where the header will appear. -->
          <div class="held-header" aria-hidden="true"></div>
        }
        @if (phase() === 'answer' && answerText().trim()) {
          <!-- One raw-markdown segment: no segmentResponse, no caret. -->
          <squadbox-streamed-text
            class="answer"
            [text]="answerText()"
            [live]="true"
            [inertLinks]="inertLinks()"
            (caughtUp)="caughtUpTo.set($event)"
          />
        }
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    /* The simulation's symbol column: 32px + 8px, closing when thinking ends. */
    .turn {
      display: grid;
      grid-template-columns: 32px minmax(0, 1fr);
      column-gap: 8px;
      align-items: start;
      transition:
        grid-template-columns var(--motion-base) var(--motion-ease),
        column-gap var(--motion-base) var(--motion-ease);
    }

    .turn.folded {
      grid-template-columns: 0px minmax(0, 1fr);
      column-gap: 0;
    }

    /* 5px centres the 30.81px mark on the 33px header (4px block padding). */
    .mark {
      padding-top: 5px;
      overflow: hidden;
      transition: opacity var(--motion-base) var(--motion-ease);
    }

    .turn.folded .mark {
      opacity: 0;
    }

    .body {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }

    /* The block's own padding (4px) + its 33px header + 4px. */
    .held-header {
      height: 41px;
    }

    .header-in {
      animation: turn-fade-in var(--motion-base) var(--motion-ease) both;
    }

    /* The simulation's answer inset: text starts level with the header. */
    .answer {
      display: block;
      padding: 8px;
    }

    @keyframes turn-fade-in {
      from {
        opacity: 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .turn,
      .mark {
        transition: none;
      }
      .header-in {
        animation: none;
      }
    }
  `,
})
export class ThinkingTurnComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  /** The live stream — or, once `closed`, its last snapshot. */
  public readonly stream = input.required<ChatStreamState>();
  /** The answer has landed; this turn only finishes its choreography. */
  public readonly closed = input(false);
  /** The saved reply's text once `closed` — what takes this turn's place. */
  public readonly final = input<string | null>(null);
  /**
   * SB-3978 B5-T21 (KD-37, plan A3): nothing in this turn navigates — its
   * answer's links and its trace's page hosts read as text. Fail-closed: on
   * unless the page knows the chat is a general or skill chat.
   */
  public readonly inertLinks = input(true);
  /** The choreography is over: the page can show the saved reply. */
  public readonly settled = output<void>();

  protected readonly phase = signal<Phase>('mark');
  /** How many of the stream's steps are on screen. */
  private readonly revealed = signal(0);
  protected readonly lastGroupOpen = signal(true);
  /** Handed to the block as its `answerStartedAt`: set at the fold, not before. */
  protected readonly foldedAnswerAt = signal<number | null>(null);
  /** The length of answer text fully on screen, -1 while the reveal is behind. */
  protected readonly caughtUpTo = signal(-1);

  /**
   * What the answer reveals: the streamed text, then — once the reply has
   * landed and it carries the streamed text as its start — the reply itself,
   * so the Sources block core-ai appends streams in like the rest and the
   * handoff shows exactly what is already on screen. A reply that differs
   * keeps the streamed text; the saved one replaces it at the handoff.
   */
  protected readonly answerText = computed(() => {
    const streamed = this.stream().text;
    const final = this.final();
    return final?.startsWith(streamed.trimEnd()) ? final : streamed;
  });

  /**
   * From the simulation's header beat on — a turn still thinking shows its
   * ticking header before any step. A trace that folds with no step at all
   * keeps one line, how long it thought — the line its saved reply shows.
   */
  protected readonly headerShown = computed(() => this.phase() !== 'mark');
  /**
   * The revealed steps, as they should read now: a row that finished before its
   * live wording had its {@link BEATS.liveHold} still shows that wording, and a
   * turn thinking with no step yet shows the placeholder row (never saved).
   */
  protected readonly shownSteps = computed<ThinkingStep[]>(() => {
    this.clock();
    const steps = this.stream().steps.slice(0, this.revealed());
    if (steps.length === 0) {
      return this.placeholder() && this.phase() === 'thinking'
        ? [this.placeholderStep]
        : [];
    }
    const now = Date.now();
    return steps.map((step) => {
      const live = this.liveSeen.get(step.id);
      return live && step.status !== 'started' && now - live.at < BEATS.liveHold
        ? { ...step, status: 'started', label: live.label }
        : step;
    });
  });
  protected readonly folded = computed(
    () => this.phase() === 'folding' || this.phase() === 'answer',
  );

  /** The placeholder row is up: the first step's beat came with no step. */
  private readonly placeholder = signal(false);
  private readonly placeholderStep: ThinkingStep = {
    id: 'placeholder',
    kind: 'reasoning',
    status: 'started',
    label: String(this.translate.instant('THINKING.STEP.PLACEHOLDER')),
    detail: '',
  };
  /** When each revealed row was first on screen live, and with which words. */
  private readonly liveSeen = new Map<string, { label: string; at: number }>();
  /** Ticks when a hold ends, so {@link shownSteps} reads the row past tense. */
  private readonly clock = signal(0);
  private holdTimer: ReturnType<typeof setTimeout> | undefined;

  private headerAt = 0;
  private lastRevealAt = 0;
  private groupClosedAt = 0;
  private foldStartedAt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private settledOnce = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      clearTimeout(this.timer);
      clearTimeout(this.holdTimer);
    });

    // Every stream frame (and every closing) re-runs the sequencer.
    effect(() => {
      this.stream();
      this.closed();
      untracked(() => this.advance());
    });

    // A finished answer settles once all of it is on screen (or it had none):
    // all of THIS text — a caught-up report for a shorter one doesn't count.
    effect((onCleanup) => {
      if (!this.closed() || this.phase() !== 'answer') {
        return;
      }
      const text = this.answerText();
      if (this.caughtUpTo() === text.length || !text.trim()) {
        this.settle();
        return;
      }
      const timeoutId = setTimeout(() => this.settle(), BEATS.maxHold);
      onCleanup(() => clearTimeout(timeoutId));
    });
  }

  /** One step of the simulation's `run()`, from wherever the turn is now. */
  private advance(): void {
    clearTimeout(this.timer);
    const now = Date.now();
    this.noteLiveRows(now);
    this.advancePhase(now);
    this.scheduleHoldTick(now);
  }

  private advancePhase(now: number): void {
    switch (this.phase()) {
      case 'mark':
        this.advanceMark(now);
        break;
      case 'thinking':
        this.advanceThinking(now);
        break;
      case 'folding':
        if (!this.later(BEATS.fold - (now - this.foldStartedAt))) {
          this.phase.set('answer');
        }
        break;
      case 'answer':
        this.reopenAfterReset();
        break;
    }
  }

  /** The answer is ready: its first words arrived, or it landed whole. */
  private answerReady(): boolean {
    return this.stream().answerStartedAt !== null || this.closed();
  }

  /** Runs `advance` again in `ms`; false when that beat is already due. */
  private later(ms: number): boolean {
    if (ms <= 0) {
      return false;
    }
    this.schedule(ms);
    return true;
  }

  private advanceMark(now: number): void {
    const stream = this.stream();
    if (stream.steps.length === 0 && this.answerReady()) {
      // A turn with no steps folds at once: the mark fades, the column closes,
      // and its trace is the one line saying how long it thought.
      this.fold(now);
      return;
    }
    // The simulation's beat: the header comes after the mark alone, whether or
    // not a step is there yet — a turn still thinking shows it thinking.
    if (this.later(BEATS.markAlone - (now - stream.startedAt))) {
      return;
    }
    this.headerAt = now;
    this.phase.set('thinking');
    this.schedule(BEATS.firstStep);
  }

  private advanceThinking(now: number): void {
    const steps = this.stream().steps;
    const revealed = this.revealed();
    if (revealed === steps.length) {
      this.awaitNextStep(now, steps.length);
      return;
    }
    // A group finishes — past tense, fold — before the next kind of step
    // appears under it.
    if (
      this.lastGroupOpen() &&
      endsOnGroup(steps, revealed) &&
      steps[revealed - 1].kind !== steps[revealed].kind
    ) {
      this.lastGroupOpen.set(false);
      this.groupClosedAt = now;
      this.schedule(BEATS.groupClose);
      return;
    }
    const stepWait =
      revealed === 0
        ? BEATS.firstStep - (now - this.headerAt)
        : BEATS.stepGap - (now - this.lastRevealAt);
    const groupWait = this.lastGroupOpen()
      ? 0
      : BEATS.groupClose - (now - this.groupClosedAt);
    if (this.later(Math.max(stepWait, groupWait))) {
      return;
    }
    this.revealed.set(revealed + 1);
    this.lastRevealAt = now;
    this.lastGroupOpen.set(true);
    this.schedule(BEATS.stepGap);
  }

  /**
   * Every step so far is on screen. A turn still thinking with none yet shows
   * the placeholder row on the first step's beat; once the answer is ready, it
   * folds — after any live row has had its hold and turned past tense.
   */
  private awaitNextStep(now: number, stepCount: number): void {
    if (!this.answerReady()) {
      if (
        stepCount === 0 &&
        !this.later(BEATS.firstStep - (now - this.headerAt))
      ) {
        this.placeholder.set(true);
      }
      return;
    }
    // A held row's own tick runs `advance` again once it reads past tense.
    if (this.holdEnd(now) !== null) {
      return;
    }
    if (!this.later(BEATS.doneBeat - (now - this.lastRevealAt))) {
      this.fold(now);
    }
  }

  /** Remember when each revealed row was first on screen live. */
  private noteLiveRows(now: number): void {
    for (const step of this.stream().steps.slice(0, this.revealed())) {
      if (
        step.status === 'started' &&
        step.label &&
        !this.liveSeen.has(step.id)
      ) {
        this.liveSeen.set(step.id, { label: step.label, at: now });
      }
    }
  }

  /** When the rows still held live may turn past tense — `null`: none is held. */
  private holdEnd(now: number): number | null {
    let end: number | null = null;
    for (const step of this.stream().steps.slice(0, this.revealed())) {
      const live = this.liveSeen.get(step.id);
      if (live && step.status !== 'started' && now - live.at < BEATS.liveHold) {
        end = Math.max(end ?? 0, live.at + BEATS.liveHold);
      }
    }
    return end;
  }

  private scheduleHoldTick(now: number): void {
    clearTimeout(this.holdTimer);
    const end = this.holdEnd(now);
    if (end === null) {
      return;
    }
    this.holdTimer = setTimeout(() => {
      // The held rows read past tense from now: the fold's beat counts from here.
      this.lastRevealAt = Date.now();
      this.clock.update((tick) => tick + 1);
      this.advance();
    }, end - now);
  }

  /** An `answer_reset` took the stream back to thinking: reopen the trace. */
  private reopenAfterReset(): void {
    if (this.answerReady() || this.stream().steps.length === 0) {
      return;
    }
    this.foldedAnswerAt.set(null);
    this.caughtUpTo.set(-1);
    this.phase.set('thinking');
    this.advance();
  }

  /** The simulation's "all at once": fold, fade, swap the header. */
  private fold(now: number): void {
    this.foldStartedAt = now;
    this.foldedAnswerAt.set(this.stream().answerStartedAt ?? now);
    this.phase.set('folding');
    this.schedule(BEATS.fold);
  }

  private schedule(ms: number): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.advance(), Math.max(0, ms));
  }

  private settle(): void {
    if (!this.settledOnce) {
      this.settledOnce = true;
      this.settled.emit();
    }
  }
}
